import { Stack, Typography, useTheme } from '@mui/material';
import Chart from 'react-apexcharts';
import { useGatewayReadings } from 'src/features/sites/hooks';
import { chartSeries, getChartOptions } from '../../config';
import { NoDataAvailableBox } from '../NoDataAvailableBox';
import { cardChartLabel } from '../style';
import { WidgetWrapper } from '../WidgetWrapper';
import { UserTypes } from 'src/features/authentication/types';

type Props = {
  type: 'continuous' | 'discrete';
  title: string;
  unit: string;
  isEditMode?: boolean;
  widgetId?: string;
  values: any[] | null;
  toggleAddOrRemoveHandler?: () => void;
  testId?: string;
  gatewayInput: any;
  customerId?: string;
  user?: UserTypes;
};

// Appends a synthetic point at the end of the fetched window holding the last known
// value, so an on-change series renders as a continuous line up to the right edge of
// the visible range instead of ending at the last change.
//
// endDate is an absolute ISO string (toISOString(), ends in "Z"), so it is already in
// the same true-UTC space that formatNewData's timestamps end up in once its offset
// dance has resolved the naive reading strings. We therefore use endDate as-is and must
// NOT re-apply the local timezone offset here: doing so pushes the synthetic point hours
// into the future and makes the chart display future timestamps. Mirrors extendOnChangeData
// in the trends line chart. Never inserts a point that isn't strictly after the last one
// (keeps the stepline monotonic).
const carryLastValueForward = (data: any[], endDate?: string | null): any[] => {
  if (!data.length) return data;

  const last = data[data.length - 1];
  const end = endDate ? new Date(endDate) : new Date();

  if (end.getTime() <= new Date(last.timestamp).getTime()) return data;

  return [...data, { ...last, timestamp: end.toString() }];
};

export const TrendWidget = ({
  type,
  title,
  unit,
  isEditMode,
  widgetId,
  values,
  toggleAddOrRemoveHandler,
  testId,
  gatewayInput,
}: Props) => {
  const theme = useTheme();
  const gatewayId = gatewayInput?.gatewayId;
  const tagId = gatewayInput?.id;
  const { readings, isDataReady, display, endDate } = useGatewayReadings({
    gatewayId: gatewayId || '',
    tagId: tagId || '',
    isOnChange: type === 'discrete',
  });

  const rawInputNameReadings = readings && readings[tagId];
  const readingsValues = rawInputNameReadings && rawInputNameReadings.values;
  const newData = readingsValues && Object.entries(readingsValues);

  const formatNewData = newData?.flatMap((reading: any) => {
    const formattedTimestamp = reading[0] && new Date(reading[0]);
    const localTime = new Date(
      formattedTimestamp.getTime() - formattedTimestamp.getTimezoneOffset() * 60 * 1000
    );
    return {
      timestamp: new Date(localTime).toString(),
      input: +reading[1] && reading[1],
      inputName: rawInputNameReadings && rawInputNameReadings.displayName,
      rawUnit: rawInputNameReadings && rawInputNameReadings.rawUnit,
    };
  });

  const combinedValues = [...(formatNewData || []), ...(values || [])];

  const sortedCombinedValues = combinedValues.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const sortedData = sortedCombinedValues.length ? sortedCombinedValues : [];

  // Mode/state/alarm tags are on-change: a point is only emitted when the value
  // changes, so the last reading holds until now. Carry it forward to the current
  // time so the stepline stays continuous to the right edge instead of stopping at
  // the last change. Mirrors the on-change handling in the trends line chart.
  const data = type === 'discrete' ? carryLastValueForward(sortedData, endDate) : sortedData;

  // For discrete tags with pre-defined values, show every configured option on the
  // y-axis, even those with no data point in the current time frame.
  const predefinedOptionValues = display?.labels
    ? Object.keys(display.labels)
        .map(Number)
        .filter((v) => !isNaN(v))
    : [];

  const uniqueDataValues =
    type === 'discrete'
      ? [
          ...new Set([
            ...predefinedOptionValues,
            ...data.map((d) => Number(d.input)).filter((v) => !isNaN(v)),
          ]),
        ]
      : undefined;
  const options = getChartOptions(type, display, uniqueDataValues);
  const series = isDataReady ? chartSeries(data) : [];

  const noFormatedData = formatNewData?.length === 0 || !formatNewData;
  const noDataAvailable = !values && noFormatedData;

  const dataTestId = series[0]?.data?.length ? 'data-available' : 'no-data';

  return (
    <WidgetWrapper
      title={title}
      isEditMode={isEditMode}
      widgetId={widgetId!}
      toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
      testId={testId}
      isDataReady={isDataReady}
      isTrendWidget={true}
      options={options}
      series={series}
      typeChart="area"
      unit={unit}
      noDataAvailable={noDataAvailable}
    >
      <Stack sx={cardChartLabel} data-sm={dataTestId}>
        <Typography variant="caption" sx={{ color: theme.palette.grey[500] }}>
          {unit}
        </Typography>
      </Stack>
      {noDataAvailable ? (
        <NoDataAvailableBox />
      ) : (
        <Stack sx={{ flex: 1, minHeight: '200px' }}>
          <Chart options={options} series={series} type="area" width="100%" height="100%" />
        </Stack>
      )}
    </WidgetWrapper>
  );
};
